import { eventChannel } from 'redux-saga';
import { fork, put, select, take, takeEvery } from 'redux-saga/effects';
import * as SimplePeer from 'simple-peer';

import * as actions from '../actions';
import { getVideo, getVisitor } from '../selectors/conversation';
import { StartVideoAction } from '../typedefs/actions';
import * as actionTypes from '../typedefs/actionTypes';
import { VideoState, VisitorState } from '../typedefs/store';
import { rateLimit } from '../util/index';
import { getSubscription } from './websocketSaga';

let maxWaitMs = 40000;
let connectionTimeout: NodeJS.Timer;
let remoteStream: MediaStream;
let localStream: MediaStream;
let visitor: VisitorState;
let videoState: VideoState;
let peer: SimplePeer.Instance;
let onPeerSignalReceived: any;
let primaryNode: HTMLVideoElement;
let secondaryNode: HTMLVideoElement;

export function* startVideo(action: StartVideoAction) {
    try {
        primaryNode = action.payload.primaryNode;
        secondaryNode = action.payload.secondaryNode;

        let visitor = yield select(getVisitor);
        let videoState = yield select(getVideo);
        const subscription = getSubscription();

        if (!subscription || !videoState.request) return;

        const visitorPeerId = makePeerId(visitor.visitorId);
        const agentPeerId = videoState.request.peerId;

        localStream = yield getAndAttachVideo(primaryNode);

        yield put(actions.localVideoActive(true));

        peer = createPeer(localStream, videoState.request.iceServers);

        const onPusherSignalReceived = (signal: any) => {
            if (!peer) return;
            try {
                peer.signal(signal.data);
            } catch (e) {
                // ignore error signaling, the peer might have been destroyed
            }
        };

        onPeerSignalReceived = rateLimit((signal: any) => {
            if (!signal) return;
            subscription.trigger(`client-signal-${agentPeerId}`, { data: signal });
        }, 120);

        // setup listeners
        subscription.bind(`client-signal-${visitorPeerId}`, onPusherSignalReceived);
        peer.on('signal', onPeerSignalReceived);
        yield fork(watchPeer);

        // READY GO! tell agent that we accepted the video request and are ready to start the handshake
        subscription.trigger('client-video-request-accepted', { peerId: visitorPeerId });

        yield takeEvery([actionTypes.END_VIDEO, actionTypes.TOGGLE_MINIMIZED], endVideo);
        yield takeEvery(actionTypes.TOGGLE_AUDIO_STREAM, () => {
            if (!localStream) return;
            localStream.getAudioTracks().forEach(stream => {
                stream.enabled = !stream.enabled;
            });
        });
        yield takeEvery(actionTypes.TOGGLE_VIDEO_STREAM, () => {
            if (!localStream) return;
            localStream.getVideoTracks().forEach(stream => {
                stream.enabled = !stream.enabled;
            });
        });
    } catch (e) {
        yield put(actions.localVideoActive(false));
        yield put(actions.remoteVideoStreaming(false));
        yield put(actions.remoteVideoActive(false));
        yield put(actions.videoConnectionError(e.toString()));
        killVideo();
    }
}

function* watchPeer() {
    const chan = eventChannel(emitter => {
        peer.on('connect', () => emitter({ event: 'connect' }));
        peer.on('stream', data => emitter({ event: 'stream', data }));
        peer.on('close', () => emitter({ event: 'close' }));
        peer.on('error', data => emitter({ event: 'error', data }));
        peer.on('iceConnectionStateChange', data => emitter({ event: 'iceChange', data }));
        connectionTimeout = global.setTimeout(() => emitter({ event: 'timeout' }), maxWaitMs);

        return () => peer.end();
    });

    while (true) {
        const { event, data } = yield take(chan);
        switch (event) {
            case 'connect':
                yield put(actions.remoteVideoActive(true));
                global.clearTimeout(connectionTimeout);

                break;
            case 'stream':
                remoteStream = data;

                yield put(actions.remoteVideoStreaming(true));

                // attach video stream to HTML video tags
                attachStreamToNode(remoteStream, primaryNode);
                attachStreamToNode(localStream, secondaryNode, true);

                // discard queued signals when a stream is connected
                onPeerSignalReceived('CLEAR_QUEUE');

                break;
            case 'close':
                yield put(actions.localVideoActive(false));
                yield put(actions.remoteVideoStreaming(false));
                yield put(actions.remoteVideoActive(false));
                yield put(actions.remoteVideoClosed());

                killVideo();

                break;
            case 'error':
            case 'timeout':
                yield put(actions.localVideoActive(false));
                yield put(actions.remoteVideoStreaming(false));
                yield put(actions.remoteVideoActive(false));
                yield put(actions.videoConnectionError(data));
                killVideo();
                break;
            case 'iceChange':
                if (data === 'complete') clearTimeout(connectionTimeout);
                break;
            case 'timeout':
                break;
            default:
            // ignore
        }
    }
}

function killVideo() {
    stopStream(localStream);
    stopStream(remoteStream);
    if (peer) peer.destroy();
}

function* endVideo() {
    killVideo();
    yield put(actions.closeVideoPanel());
}

function* getAndAttachVideo(videoNode: HTMLVideoElement) {
    let stream = yield navigator.mediaDevices.getUserMedia({ video: true, audio: true });
    attachStreamToNode(stream, videoNode, true);
    return stream;
}

function attachStreamToNode(stream: MediaStream, node: HTMLVideoElement, muted: boolean = false) {
    node.srcObject = stream;
    node.muted = muted;
    node.autoplay = true;
    node.volume = muted ? 0 : 1;
}

function makePeerId(visitorId: number) {
    return `${Math.ceil(Math.random() * 1000000)}V${visitorId}`;
}

function createPeer(stream: MediaStream | any, iceServers: any) {
    return new SimplePeer({
        initiator: false,
        stream,
        trickle: true,
        config: { iceServers },
    });
}

function stopStream(stream?: MediaStream) {
    if (!stream) return;

    for (let i = 0; i < stream.getTracks().length; i++) {
        stream.getTracks()[i].stop();
    }
}

export default function* formSaga() {
    yield takeEvery(actionTypes.START_VIDEO, startVideo);
}



// WEBPACK FOOTER //
// ./~/tslint-loader!./src/sagas/videoSaga.ts